import { Shell } from '@/components/Shell';
import { type VehicleRow, adminApi } from '@/lib/api';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, createFileRoute, redirect, useNavigate } from '@tanstack/react-router';
import { useRef, useState } from 'react';

function slugify(s: string) {
  return s
    .toLowerCase()
    .normalize('NFD')
    .replace(/\p{M}/gu, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '');
}

export const Route = createFileRoute('/fleet/new')({
  beforeLoad: async () => {
    try {
      const me = await adminApi.me();
      if (me.role !== 'admin') throw redirect({ to: '/login' });
    } catch {
      throw redirect({ to: '/login' });
    }
  },
  component: NewVehiclePage,
});

interface PendingPhoto {
  id: string;
  file: File;
  previewUrl: string;
  status: 'pending' | 'uploading' | 'done' | 'error';
  error?: string;
}

const STATUS_BG: Record<PendingPhoto['status'], string> = {
  pending: 'rgba(0,0,0,.55)',
  uploading: 'var(--lv-warn)',
  done: 'var(--lv-ok)',
  error: 'var(--lv-err)',
};

function NewVehiclePage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const models = useQuery({ queryKey: ['admin', 'models'], queryFn: () => adminApi.models() });

  const [modelId, setModelId] = useState('');
  const [newModel, setNewModel] = useState(false);
  const [brand, setBrand] = useState('');
  const [modelName, setModelName] = useState('');
  const [bodyType, setBodyType] = useState<'suv' | 'sedan' | 'pickup'>('suv');
  const [seats, setSeats] = useState(5);
  const [transmission, setTransmission] = useState<'AT' | 'MT'>('AT');
  const [fuel, setFuel] = useState<'gasoline' | 'diesel' | 'hybrid'>('gasoline');

  const [plate, setPlate] = useState('');
  const [year, setYear] = useState(new Date().getFullYear());
  const [color, setColor] = useState('#234948');
  const [km, setKm] = useState(0);
  const [vin, setVin] = useState('');
  const [status, setStatus] = useState<VehicleRow['status']>('draft');
  const [eurPerDay, setEurPerDay] = useState(45);
  const [cvePerDay, setCvePerDay] = useState(5000);
  const [eurCaucao, setEurCaucao] = useState(200);
  const [cveCaucao, setCveCaucao] = useState(22000);
  const [weeklyDiscountPct, setWeeklyDiscountPct] = useState(0);

  const [photos, setPhotos] = useState<PendingPhoto[]>([]);
  const [dragOver, setDragOver] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);
  const [stagedError, setStagedError] = useState<string | null>(null);
  const [overallError, setOverallError] = useState<string | null>(null);

  function stagePhotos(files: FileList | File[]) {
    setStagedError(null);
    const next: PendingPhoto[] = [];
    for (const f of Array.from(files)) {
      if (!['image/jpeg', 'image/png', 'image/webp'].includes(f.type)) {
        setStagedError(`"${f.name}" — formato não suportado (apenas JPEG / PNG / WebP).`);
        continue;
      }
      if (f.size > 10 * 1024 * 1024) {
        setStagedError(`"${f.name}" — excede 10 MB.`);
        continue;
      }
      next.push({
        id: `${f.name}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        file: f,
        previewUrl: URL.createObjectURL(f),
        status: 'pending',
      });
    }
    setPhotos((prev) => [...prev, ...next]);
  }

  function removePhoto(id: string) {
    setPhotos((prev) => {
      const target = prev.find((p) => p.id === id);
      if (target) URL.revokeObjectURL(target.previewUrl);
      return prev.filter((p) => p.id !== id);
    });
  }

  const create = useMutation({
    mutationFn: async () => {
      let effectiveModelId = modelId;
      if (newModel) {
        const fullName = `${brand.trim()} ${modelName.trim()}`.trim();
        const baseSlug = slugify(fullName);
        const slug = { pt: baseSlug, en: baseSlug, fr: baseSlug, es: baseSlug, de: baseSlug };
        const nameTr = { pt: fullName, en: fullName, fr: fullName, es: fullName, de: fullName };
        const descTr = {
          pt: fullName,
          en: fullName,
          fr: fullName,
          es: fullName,
          de: fullName,
        };
        const created = await adminApi.createModel({
          slug,
          name: nameTr,
          description: descTr,
          category: bodyType,
          bodyType,
          seats,
          transmission,
          fuel,
          defaultColor: /^#[0-9a-fA-F]{6}$/.test(color) ? color : null,
        });
        effectiveModelId = created.id;
        await qc.invalidateQueries({ queryKey: ['admin', 'models'] });
      }

      const { id } = await adminApi.createVehicle({
        modelId: effectiveModelId,
        plate,
        year,
        color: color || undefined,
        km,
        status,
        pricing: { eurPerDay, cvePerDay, eurCaucao, cveCaucao, weeklyDiscountPct },
      });
      if (vin) {
        await adminApi.patchVehicle(id, { vin } as Record<string, unknown> as never);
      }
      for (let i = 0; i < photos.length; i++) {
        const p = photos[i]!;
        setPhotos((prev) => prev.map((x) => (x.id === p.id ? { ...x, status: 'uploading' } : x)));
        try {
          await adminApi.uploadPhoto(id, p.file);
          setPhotos((prev) => prev.map((x) => (x.id === p.id ? { ...x, status: 'done' } : x)));
        } catch (err) {
          setPhotos((prev) =>
            prev.map((x) => (x.id === p.id ? { ...x, status: 'error', error: (err as Error).message } : x)),
          );
        }
      }
      return id;
    },
    onSuccess: (id) => {
      navigate({ to: '/fleet/$id', params: { id } });
    },
    onError: (err) => setOverallError((err as Error).message),
  });

  const modelOk = newModel ? brand.trim().length >= 1 && modelName.trim().length >= 1 : !!modelId;
  const canSubmit = modelOk && plate.length >= 2 && eurPerDay > 0 && cvePerDay > 0;
  const photosWithErrors = photos.filter((p) => p.status === 'error').length;
  const allDone = photos.length > 0 && photos.every((p) => p.status === 'done');

  return (
    <Shell page="NOVA VIATURA" sub="FROTA · ADICIONAR AO INVENTÁRIO">
      <form
        onSubmit={(e) => {
          e.preventDefault();
          setOverallError(null);
          create.mutate();
        }}
        style={{ display: 'grid', gridTemplateColumns: '1fr 1.4fr', gap: 16, alignItems: 'start' }}
      >
        {/* ── Left: details ── */}
        <div style={{ display: 'grid', gap: 16 }}>
          {/* Identity */}
          <div style={{ background: '#fff', border: '1px solid var(--lv-line)', padding: 24 }}>
            <div className="label" style={{ color: 'var(--lv-brand)', marginBottom: 16 }}>
              IDENTIDADE
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <Field label="Modelo" full>
                <div style={{ display: 'flex', gap: 8, alignItems: 'stretch' }}>
                  {!newModel ? (
                    <select
                      value={modelId}
                      onChange={(e) => setModelId(e.target.value)}
                      required
                      style={{ ...input, flex: 1 }}
                    >
                      <option value="">— escolha —</option>
                      {models.data?.items.map((m) => (
                        <option key={m.id} value={m.id}>
                          {m.nameLocale} · {m.bodyType}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <div style={{ flex: 1, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                      <input
                        type="text"
                        value={brand}
                        onChange={(e) => setBrand(e.target.value)}
                        placeholder="Marca (ex. Renault)"
                        required
                        style={input}
                      />
                      <input
                        type="text"
                        value={modelName}
                        onChange={(e) => setModelName(e.target.value)}
                        placeholder="Modelo (ex. Duster)"
                        required
                        style={input}
                      />
                    </div>
                  )}
                  <button
                    type="button"
                    onClick={() => {
                      setNewModel((v) => !v);
                      if (newModel) {
                        setBrand('');
                        setModelName('');
                      } else {
                        setModelId('');
                      }
                    }}
                    style={{
                      background: newModel ? 'var(--lv-surface-soft)' : '#fff',
                      color: 'var(--lv-ink)',
                      border: '1px solid var(--lv-line)',
                      padding: '0 14px',
                      fontSize: 11,
                      fontWeight: 700,
                      letterSpacing: '0.06em',
                      textTransform: 'uppercase',
                      cursor: 'pointer',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {newModel ? 'Existente' : '+ Novo'}
                  </button>
                </div>
              </Field>
              {newModel && (
                <>
                  <Field label="Carroçaria">
                    <select
                      value={bodyType}
                      onChange={(e) => setBodyType(e.target.value as typeof bodyType)}
                      style={input}
                    >
                      <option value="suv">SUV</option>
                      <option value="sedan">Sedan</option>
                      <option value="pickup">Pickup</option>
                    </select>
                  </Field>
                  <Field label="Lugares">
                    <input
                      type="number"
                      value={seats}
                      onChange={(e) => setSeats(Number(e.target.value))}
                      min={2}
                      max={9}
                      style={input}
                    />
                  </Field>
                  <Field label="Transmissão">
                    <select
                      value={transmission}
                      onChange={(e) => setTransmission(e.target.value as typeof transmission)}
                      style={input}
                    >
                      <option value="AT">Automática</option>
                      <option value="MT">Manual</option>
                    </select>
                  </Field>
                  <Field label="Combustível">
                    <select
                      value={fuel}
                      onChange={(e) => setFuel(e.target.value as typeof fuel)}
                      style={input}
                    >
                      <option value="gasoline">Gasolina</option>
                      <option value="diesel">Gasóleo</option>
                      <option value="hybrid">Híbrido</option>
                    </select>
                  </Field>
                </>
              )}
              <Field label="Matrícula">
                <input
                  type="text"
                  value={plate}
                  onChange={(e) => setPlate(e.target.value.toUpperCase())}
                  required
                  maxLength={16}
                  style={{ ...input, fontFamily: 'var(--lv-font-mono)' }}
                />
              </Field>
              <Field label="Ano">
                <input
                  type="number"
                  value={year}
                  onChange={(e) => setYear(Number(e.target.value))}
                  min={1990}
                  max={2100}
                  required
                  style={input}
                />
              </Field>
              <Field label="Cor">
                <div style={{ display: 'flex', gap: 8, alignItems: 'stretch' }}>
                  <input
                    type="color"
                    value={/^#[0-9a-fA-F]{6}$/.test(color) ? color : '#234948'}
                    onChange={(e) => setColor(e.target.value)}
                    style={{
                      width: 44,
                      padding: 0,
                      border: '1px solid var(--lv-line)',
                      background: '#fff',
                      cursor: 'pointer',
                    }}
                    aria-label="Seleccionar cor"
                  />
                  <input
                    type="text"
                    value={color}
                    onChange={(e) => setColor(e.target.value)}
                    placeholder="#234948"
                    style={{ ...input, flex: 1, fontFamily: 'var(--lv-font-mono)' }}
                  />
                </div>
              </Field>
              <Field label="Quilómetros">
                <input
                  type="number"
                  value={km}
                  onChange={(e) => setKm(Number(e.target.value))}
                  min={0}
                  style={input}
                />
              </Field>
              <Field label="VIN">
                <input
                  type="text"
                  value={vin}
                  onChange={(e) => setVin(e.target.value)}
                  maxLength={32}
                  style={{ ...input, fontFamily: 'var(--lv-font-mono)' }}
                />
              </Field>
              <Field label="Estado">
                <select
                  value={status}
                  onChange={(e) => setStatus(e.target.value as VehicleRow['status'])}
                  style={input}
                >
                  <option value="draft">Rascunho</option>
                  <option value="active">Activa</option>
                  <option value="maintenance">Manutenção</option>
                </select>
              </Field>
            </div>
          </div>

          {/* Pricing */}
          <div style={{ background: '#fff', border: '1px solid var(--lv-line)', padding: 24 }}>
            <div className="label" style={{ color: 'var(--lv-brand)', marginBottom: 16 }}>
              PREÇO · CAUÇÃO
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <Field label="EUR / dia">
                <input
                  type="number"
                  value={eurPerDay}
                  onChange={(e) => setEurPerDay(Number(e.target.value))}
                  min={1}
                  step={1}
                  required
                  style={input}
                />
              </Field>
              <Field label="CVE / dia">
                <input
                  type="number"
                  value={cvePerDay}
                  onChange={(e) => setCvePerDay(Number(e.target.value))}
                  min={1}
                  step={1}
                  required
                  style={input}
                />
              </Field>
              <Field label="Caução EUR">
                <input
                  type="number"
                  value={eurCaucao}
                  onChange={(e) => setEurCaucao(Number(e.target.value))}
                  min={0}
                  step={1}
                  style={input}
                />
              </Field>
              <Field label="Caução CVE">
                <input
                  type="number"
                  value={cveCaucao}
                  onChange={(e) => setCveCaucao(Number(e.target.value))}
                  min={0}
                  step={1}
                  style={input}
                />
              </Field>
              <Field label="Desc. semanal %" full>
                <input
                  type="number"
                  value={weeklyDiscountPct}
                  onChange={(e) => setWeeklyDiscountPct(Number(e.target.value))}
                  min={0}
                  max={50}
                  step={1}
                  style={input}
                />
              </Field>
            </div>
          </div>
        </div>

        {/* ── Right: photos ── */}
        <div style={{ background: '#fff', border: '1px solid var(--lv-line)', padding: 24 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
            <div className="label" style={{ color: 'var(--lv-brand)' }}>
              FOTOS
            </div>
            <span style={{ fontSize: 11, color: 'var(--lv-gray-500)' }}>{photos.length} foto(s) na fila</span>
          </div>
          <div className="h-rule" style={{ marginTop: 8, marginBottom: 16 }} />

          {/* Drop zone */}
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            onDragOver={(e) => {
              e.preventDefault();
              setDragOver(true);
            }}
            onDragLeave={() => setDragOver(false)}
            onDrop={(e) => {
              e.preventDefault();
              setDragOver(false);
              if (e.dataTransfer.files.length) stagePhotos(e.dataTransfer.files);
            }}
            style={{
              width: '100%',
              padding: '40px 24px',
              border: dragOver ? '2px dashed var(--lv-brand)' : '2px dashed var(--lv-line)',
              background: dragOver ? '#eaeeed' : 'var(--lv-surface-soft)',
              color: dragOver ? 'var(--lv-brand)' : 'var(--lv-gray-500)',
              fontSize: 13,
              cursor: 'pointer',
              textAlign: 'center',
              transition: 'border-color 0.1s, background 0.1s, color 0.1s',
            }}
          >
            <div style={{ marginBottom: 6 }}>Arrasta fotos aqui ou clica para escolher</div>
            <span style={{ fontSize: 11, letterSpacing: '0.06em', textTransform: 'uppercase' }}>
              JPEG · PNG · WebP · até 10 MB cada
            </span>
          </button>

          <input
            ref={fileRef}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            multiple
            onChange={(e) => {
              if (e.target.files?.length) stagePhotos(e.target.files);
              if (e.target) e.target.value = '';
            }}
            style={{ display: 'none' }}
          />

          {stagedError && (
            <div
              style={{
                marginTop: 8,
                padding: '8px 12px',
                background: '#fce4e4',
                color: 'var(--lv-err)',
                fontSize: 12,
                borderLeft: '3px solid var(--lv-err)',
              }}
            >
              {stagedError}
            </div>
          )}

          {photos.length > 0 && (
            <ul
              style={{
                marginTop: 16,
                listStyle: 'none',
                padding: 0,
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))',
                gap: 8,
              }}
            >
              {photos.map((p) => (
                <li
                  key={p.id}
                  style={{
                    position: 'relative',
                    border: '1px solid var(--lv-line)',
                    background: 'var(--lv-brand-deep)',
                    aspectRatio: '4 / 3',
                    overflow: 'hidden',
                  }}
                >
                  <img
                    src={p.previewUrl}
                    alt=""
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      opacity: p.status === 'done' ? 1 : 0.55,
                    }}
                  />
                  <div
                    style={{
                      position: 'absolute',
                      inset: 0,
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'space-between',
                      padding: 6,
                    }}
                  >
                    <div
                      style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}
                    >
                      <span
                        style={{
                          background: STATUS_BG[p.status],
                          color: '#fff',
                          fontSize: 9,
                          fontWeight: 700,
                          letterSpacing: '0.06em',
                          padding: '2px 6px',
                          textTransform: 'uppercase',
                        }}
                      >
                        {p.status}
                      </span>
                      {p.status !== 'uploading' && p.status !== 'done' && (
                        <button
                          type="button"
                          onClick={() => removePhoto(p.id)}
                          style={{
                            background: '#fff',
                            color: 'var(--lv-err)',
                            border: 0,
                            padding: '2px 6px',
                            fontSize: 11,
                            fontWeight: 700,
                            cursor: 'pointer',
                          }}
                        >
                          ✕
                        </button>
                      )}
                    </div>
                    {p.error && (
                      <div
                        style={{
                          background: 'rgba(0,0,0,.7)',
                          color: '#fff',
                          fontSize: 10,
                          padding: '3px 6px',
                        }}
                      >
                        {p.error}
                      </div>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}

          <p style={{ marginTop: 14, fontSize: 11, color: 'var(--lv-gray-500)', lineHeight: 1.5 }}>
            A capa é sempre a foto na posição 0. Podes editar a ordem e o texto alternativo (5 línguas) no
            detalhe da viatura após criar.
          </p>
        </div>

        {/* ── Footer (full width) ── */}
        <div
          style={{
            gridColumn: '1 / -1',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 16,
            paddingTop: 4,
          }}
        >
          <div style={{ fontSize: 12 }}>
            {overallError && <span style={{ color: 'var(--lv-err)', fontWeight: 700 }}>{overallError}</span>}
            {photosWithErrors > 0 && !overallError && (
              <span style={{ color: 'var(--lv-warn)', fontWeight: 700 }}>
                {photosWithErrors} foto(s) falharam — podes re-enviar no detalhe.
              </span>
            )}
            {allDone && !overallError && (
              <span style={{ color: 'var(--lv-ok)', fontWeight: 700 }}>✓ Todas as fotos enviadas.</span>
            )}
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <Link
              to="/fleet"
              style={{
                background: '#fff',
                color: 'var(--lv-ink)',
                border: '1px solid var(--lv-line)',
                padding: '11px 22px',
                fontSize: 12,
                fontWeight: 700,
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
                textDecoration: 'none',
                display: 'inline-flex',
                alignItems: 'center',
              }}
            >
              Cancelar
            </Link>
            <button
              type="submit"
              disabled={!canSubmit || create.isPending}
              style={{
                background: 'var(--lv-brand)',
                color: '#fff',
                opacity: canSubmit && !create.isPending ? 1 : 0.4,
                border: 0,
                padding: '11px 22px',
                fontSize: 12,
                fontWeight: 700,
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
                cursor: canSubmit && !create.isPending ? 'pointer' : 'not-allowed',
              }}
            >
              {create.isPending ? 'A criar…' : 'Criar viatura'}
            </button>
          </div>
        </div>
      </form>
    </Shell>
  );
}

function Field({
  label,
  children,
  full,
}: {
  label: string;
  children: React.ReactNode;
  full?: boolean;
}) {
  return (
    <label style={{ display: 'block', gridColumn: full ? '1 / -1' : undefined }}>
      <span
        style={{
          display: 'block',
          fontSize: 10,
          fontWeight: 700,
          letterSpacing: '0.08em',
          textTransform: 'uppercase',
          color: 'var(--lv-gray-500)',
          marginBottom: 5,
        }}
      >
        {label}
      </span>
      {children}
    </label>
  );
}

const input: React.CSSProperties = {
  width: '100%',
  padding: '10px 12px',
  border: '1px solid var(--lv-line)',
  background: '#fff',
  fontSize: 13,
  borderRadius: 0,
};
